import React, { useState, useEffect, useCallback } from 'react';
import DocumentChecklist from '../components/DocumentChecklist';
import { ChecklistItem, getChecklistSectionId } from '../utils/documentChecklist';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  Grid,
  Tooltip,
  Autocomplete,
  createFilterOptions,
  Divider,
} from '@mui/material';
import {
  Add,
  Delete,
  Download,
  Visibility,
  PictureAsPdf,
  Close,
  HelpOutline,
  UploadFile,
} from '@mui/icons-material';
import { documentoService, Documento } from '../services/documentoService';
import { socioService, Socio } from '../services/socioService';
import { documentosVisiveisParaSocio, ehAssociado } from '../utils/plano';

const TIPOS_DOCUMENTO = [
  { value: 'CR', label: 'CR' },
  { value: 'CRAF', label: 'CRAF' },
  { value: 'GT', label: 'GT' },
  { value: 'RG/CNH', label: 'RG/CNH' },
  { value: 'SIMAF', label: 'SIMAF' },
  { value: 'Autorização Fazenda', label: 'Autorização Fazenda' },
  { value: 'Certificado regularidade IBAMA', label: 'Certificado regularidade IBAMA' },
  { value: 'Laudo Veterinario', label: 'Laudo Veterinario' },
  { value: 'Outros', label: 'Outros' },
];

const Documentos: React.FC = () => {
  const [socios, setSocios] = useState<Socio[]>([]);
  const [socioSelecionado, setSocioSelecionado] = useState<Socio | null>(null);
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [documentoPreview, setDocumentoPreview] = useState<Documento | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [editingDocumento, setEditingDocumento] = useState<Documento | null>(null);
  const [dataValidoAteSocio, setDataValidoAteSocio] = useState<string | null | undefined>(undefined);

  const [formData, setFormData] = useState({
    tipo_documento: '',
    arquivo: null as File | null,
    data_validade: '',
    observacoes: '',
  });

  const loadSocios = useCallback(async () => {
    try {
      const data = await socioService.listar();
      setSocios(data);
    } catch (err: any) {
      setError('Erro ao carregar sócios');
    }
  }, []);

  const socioId = socioSelecionado?.id;

  const loadDocumentos = useCallback(async () => {
    if (!socioId) {
      setDocumentos([]);
      setDataValidoAteSocio(undefined);
      return;
    }

    try {
      setLoading(true);
      const socio = await socioService.obter(socioId);
      const data = await documentoService.listarPorSocio(socioId);
      setDataValidoAteSocio(socio.data_valido_ate);
      setDocumentos(documentosVisiveisParaSocio(socio.data_valido_ate, data));
    } catch (err: any) {
      setError('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  }, [socioId]);

  useEffect(() => {
    loadSocios();
  }, [loadSocios]);

  useEffect(() => {
    loadDocumentos();
  }, [loadDocumentos]);

  const handleOpenDialog = (tipo?: string, observacoes?: string) => {
    if (!socioSelecionado) {
      setError('Selecione um sócio primeiro');
      return;
    }
    setEditingDocumento(null);
    setFormData({
      tipo_documento: tipo || '',
      arquivo: null,
      data_validade: '',
      observacoes: observacoes || '',
    });
    setError('');
    setOpenDialog(true);
  };

  const scrollToDocumentSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleChecklistItemClick = (item: ChecklistItem) => {
    if (item.waived) return;
    if (item.ok && item.documentoId) {
      const sectionId = getChecklistSectionId(item);
      if (sectionId) {
        scrollToDocumentSection(sectionId);
      }
      return;
    }
    if (!item.uploadTipo) return;
    if (item.documentoId) {
      const doc = documentos.find((d) => d.id === item.documentoId);
      if (doc?.status === 'Vencido') {
        handleOpenReplace(doc);
        return;
      }
    }
    handleOpenDialog(item.uploadTipo, item.uploadObservacao);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDocumento(null);
    setFormData({
      tipo_documento: '',
      arquivo: null,
      data_validade: '',
      observacoes: '',
    });
    setError('');
  };

  const formatDateInput = (value?: string) => {
    if (!value) return '';
    return value.includes('T') ? value.split('T')[0] : value;
  };

  const handleOpenReplace = (documento: Documento) => {
    if (!socioSelecionado) {
      setError('Selecione um sócio primeiro');
      return;
    }
    setEditingDocumento(documento);
    setFormData({
      tipo_documento: documento.tipo_documento,
      arquivo: null,
      data_validade: formatDateInput(documento.data_validade),
      observacoes: documento.observacoes || '',
    });
    setError('');
    setOpenDialog(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.type !== 'application/pdf') {
        setError('Apenas arquivos PDF são permitidos');
        return;
      }
      setFormData({ ...formData, arquivo: file });
      setError('');
    }
  };

  const handleSubmit = async () => {
    if (!socioSelecionado?.id) {
      setError('Selecione um sócio');
      return;
    }

    if (!formData.tipo_documento) {
      setError('Selecione o tipo de documento');
      return;
    }

    if (!formData.arquivo) {
      setError('Selecione um arquivo PDF');
      return;
    }

    try {
      setUploading(true);
      setError('');

      if (editingDocumento?.id) {
        await documentoService.atualizar(editingDocumento.id, {
          tipo_documento: formData.tipo_documento,
          arquivo: formData.arquivo,
          data_validade: formData.data_validade || undefined,
          observacoes: formData.observacoes || undefined,
        });
      } else {
        await documentoService.criar({
          socio_id: socioSelecionado.id,
          tipo_documento: formData.tipo_documento,
          arquivo: formData.arquivo,
          data_validade: formData.data_validade || undefined,
          observacoes: formData.observacoes || undefined,
        });
      }

      handleCloseDialog();
      loadDocumentos();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao fazer upload do documento');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja deletar este documento?')) {
      return;
    }

    try {
      await documentoService.deletar(id);
      loadDocumentos();
    } catch (err: any) {
      setError('Erro ao deletar documento');
    }
  };

  const handleDownload = async (documento: Documento) => {
    if (!documento.id) return;
    try {
      await documentoService.downloadDocumento(documento.id);
    } catch (err: any) {
      setError('Erro ao fazer download do documento');
    }
  };

  const handlePreview = async (documento: Documento) => {
    if (!documento.id) return;
    setLoadingPreview(true);
    setDocumentoPreview(documento);
    try {
      const url = await documentoService.getVisualizarBlobUrl(documento.id);
      setPreviewUrl(url);
      setPreviewDialog(true);
    } catch (err: any) {
      setError('Erro ao carregar preview do documento');
    } finally {
      setLoadingPreview(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  const decodeFileName = (name: string) => {
    try {
      return decodeURIComponent(name);
    } catch {
      return name;
    }
  };

  const truncateFileName = (name: string, maxLen = 32) => {
    const decoded = decodeFileName(name);
    if (decoded.length <= maxLen) return decoded;
    return `${decoded.substring(0, maxLen)}...`;
  };

  const documentTableColumnSx = {
    nome: {
      width: '36%',
      maxWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    upload: { width: '14%', whiteSpace: 'nowrap' },
    validade: { width: '14%', whiteSpace: 'nowrap' },
    status: { width: '14%', whiteSpace: 'nowrap' },
    acoes: { width: '22%', whiteSpace: 'nowrap' },
  };

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'Válido':
        return 'success';
      case 'Vencido':
        return 'error';
      case 'Pendente':
        return 'warning';
      default:
        return 'default';
    }
  };

  const documentosPorTipo = documentos.reduce((acc, doc) => {
    const tipo = doc.tipo_documento;
    if (!acc[tipo]) {
      acc[tipo] = [];
    }
    acc[tipo].push(doc);
    return acc;
  }, {} as Record<string, Documento[]>);

  const ordemTipos = TIPOS_DOCUMENTO.map((tipo) => tipo.value);
  const documentosOrdenados = Object.entries(documentosPorTipo).sort(
    ([tipoA], [tipoB]) => {
      const indexA = ordemTipos.indexOf(tipoA);
      const indexB = ordemTipos.indexOf(tipoB);
      const safeA = indexA === -1 ? Number.MAX_SAFE_INTEGER : indexA;
      const safeB = indexB === -1 ? Number.MAX_SAFE_INTEGER : indexB;
      return safeA - safeB;
    }
  );

  const filtroSocios = createFilterOptions<Socio>({
    stringify: (option) =>
      `${option.codigo} ${option.nome_completo} ${option.cpf || ''}`,
  });

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Documentos</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          disabled={!socioSelecionado}
        >
          Novo Documento
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {socioSelecionado && !ehAssociado(dataValidoAteSocio) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Conta <strong>visitante</strong> — exibindo 1 documento por tipo (primeiro cadastrado).
          Associados com anuidade vigente veem todos os documentos.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Selecionar Sócio
            </Typography>
            <Autocomplete
              options={socios}
              value={socioSelecionado}
              onChange={(_, value) => setSocioSelecionado(value)}
              getOptionLabel={(option) =>
                `${option.codigo} - ${option.nome_completo}${option.cpf ? ` (${option.cpf})` : ''}`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={filtroSocios}
              renderInput={(params) => (
                <TextField {...params} label="Selecione um sócio" />
              )}
            />
            {socioSelecionado && (
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary">
                  <strong>CPF:</strong> {socioSelecionado.cpf}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Total de documentos:</strong> {documentos.length}
                </Typography>
              </Box>
            )}

            {socioSelecionado && !loading && (
              <>
                <Divider sx={{ my: 2 }} />
                <DocumentChecklist
                  embedded
                  documentos={documentos}
                  onItemClick={handleChecklistItemClick}
                />
              </>
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          {!socioSelecionado ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Selecione um sócio para visualizar seus documentos
              </Typography>
            </Paper>
          ) : loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : documentos.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Nenhum documento cadastrado para este sócio
              </Typography>
            </Paper>
          ) : (
            <Box>
              {documentosOrdenados.map(([tipo, docs]) => (
                <Box
                  key={tipo}
                  id={`doc-section-${tipo}`}
                  mb={3}
                  sx={{ scrollMarginTop: 16 }}
                >
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PictureAsPdf color="error" />
                    {tipo} ({docs.length})
                  </Typography>
                  <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
                    <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={documentTableColumnSx.nome}>Nome do Arquivo</TableCell>
                          <TableCell sx={documentTableColumnSx.upload}>Data Upload</TableCell>
                          <TableCell sx={documentTableColumnSx.validade}>Validade</TableCell>
                          <TableCell sx={documentTableColumnSx.status}>Status</TableCell>
                          <TableCell align="right" sx={documentTableColumnSx.acoes}>
                            Ações
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {docs.map((doc) => (
                          <TableRow key={doc.id}>
                            <TableCell sx={documentTableColumnSx.nome}>
                              <Tooltip title={decodeFileName(doc.nome_arquivo)} arrow placement="top">
                                <Typography variant="body2" noWrap component="span">
                                  {truncateFileName(doc.nome_arquivo)}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={documentTableColumnSx.upload}>
                              {formatDate(doc.data_upload)}
                            </TableCell>
                            <TableCell sx={documentTableColumnSx.validade}>
                              {formatDate(doc.data_validade)}
                            </TableCell>
                            <TableCell sx={documentTableColumnSx.status}>
                              <Chip
                                label={doc.status || 'Pendente'}
                                color={getStatusColor(doc.status) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right" sx={documentTableColumnSx.acoes}>
                              <Box display="flex" alignItems="center" gap={0.5} justifyContent="flex-end">
                                {doc.observacoes && (
                                  <Tooltip
                                    title={doc.observacoes}
                                    arrow
                                    placement="top"
                                  >
                                    <IconButton
                                      size="small"
                                      sx={{
                                        padding: '2px',
                                        color: 'text.secondary',
                                        '&:hover': {
                                          color: 'primary.main',
                                        },
                                      }}
                                    >
                                      <HelpOutline fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <IconButton
                                  size="small"
                                  onClick={() => handlePreview(doc)}
                                  title="Visualizar"
                                >
                                  <Visibility />
                                </IconButton>
                              {doc.status === 'Vencido' && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenReplace(doc)}
                                  title="Substituir documento vencido"
                                  color="primary"
                                >
                                  <UploadFile />
                                </IconButton>
                              )}
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownload(doc)}
                                  title="Download"
                                >
                                  <Download />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(doc.id!)}
                                  title="Deletar"
                                  color="error"
                                >
                                  <Delete />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Dialog de Upload */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDocumento ? 'Substituir Documento' : 'Novo Documento'}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {editingDocumento && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Documento vencido: selecione um novo PDF para substituir o existente.
            </Alert>
          )}
          {socioSelecionado && (
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary">
                <strong>Sócio:</strong> {socioSelecionado.codigo} - {socioSelecionado.nome_completo}
              </Typography>
            </Box>
          )}
          <FormControl fullWidth margin="normal">
            <InputLabel>Tipo de Documento</InputLabel>
            <Select
              value={formData.tipo_documento}
              label="Tipo de Documento"
              onChange={(e) => setFormData({ ...formData, tipo_documento: e.target.value })}
            >
              {TIPOS_DOCUMENTO.map((tipo) => (
                <MenuItem key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box mt={2} mb={2}>
            <input
              accept="application/pdf"
              style={{ display: 'none' }}
              id="file-upload"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="file-upload">
              <Button variant="outlined" component="span" fullWidth>
                {formData.arquivo ? formData.arquivo.name : 'Selecionar PDF'}
              </Button>
            </label>
            {formData.arquivo && (
              <Typography variant="caption" display="block" mt={1} color="text.secondary">
                Arquivo selecionado: {formData.arquivo.name} ({(formData.arquivo.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            )}
          </Box>
          <TextField
            fullWidth
            label="Data de Validade"
            margin="normal"
            type="date"
            value={formData.data_validade}
            onChange={(e) => setFormData({ ...formData, data_validade: e.target.value })}
            InputLabelProps={{ shrink: true }}
            helperText="Opcional"
          />
          <TextField
            fullWidth
            label="Observações"
            margin="normal"
            multiline
            rows={3}
            value={formData.observacoes}
            onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
            helperText="Opcional"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={uploading}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={uploading}>
            {uploading ? <CircularProgress size={20} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Preview */}
      <Dialog
        open={previewDialog}
        onClose={() => {
          if (previewUrl) {
            window.URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
          }
          setPreviewDialog(false);
        }}
        maxWidth="md"
        fullWidth
        sx={{ '& .MuiDialog-paper': { m: { xs: 1, sm: 2 } } }}
      >
        <DialogTitle>
          {documentoPreview?.nome_arquivo}
          <IconButton
            aria-label="close"
            onClick={() => {
              if (previewUrl) {
                window.URL.revokeObjectURL(previewUrl);
                setPreviewUrl(null);
              }
              setPreviewDialog(false);
            }}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {loadingPreview ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : previewUrl && documentoPreview ? (
            <Box>
              <iframe
                src={previewUrl}
                style={{ border: 'none', width: '100%', height: '60vh', minHeight: 360 }}
                title={documentoPreview.nome_arquivo}
              />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => documentoPreview && handleDownload(documentoPreview)}>
            <Download /> Download
          </Button>
          <Button
            onClick={() => {
              if (previewUrl) {
                window.URL.revokeObjectURL(previewUrl);
                setPreviewUrl(null);
              }
              setPreviewDialog(false);
            }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Documentos;
